import React, { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { api } from "@/lib/api";
import { Eye, EyeSlash } from "@phosphor-icons/react";
import { toast } from "sonner";

const MBE_LOGO = "https://customer-assets.emergentagent.com/job_mbe-bali-tracker/artifacts/8ljitr27_PROFILE%20ALL%20CHANNEL-05.jpg";

export default function Login() {
  const { login, user } = useAuth();
  const nav = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [show, setShow] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [campaign, setCampaign] = useState(null);

  React.useEffect(() => {
    if (user) nav("/dashboard");
  }, [user, nav]);

  React.useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/campaign-settings/public");
        setCampaign(data);
      } catch { /* ignore */ }
    })();
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await login(email, password);
      toast.success("Welcome back");
      nav("/dashboard");
    } catch (err) {
      toast.error(err.message || "Login failed");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen flex bg-[#0B0F19]">
      {/* Left brand panel */}
      <div className="hidden lg:flex w-1/2 relative overflow-hidden">
        <div className="absolute inset-0 bg-[#0B0F19]" />
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_50%_40%,rgba(249,115,22,0.18),transparent_55%),radial-gradient(circle_at_20%_80%,rgba(37,99,235,0.15),transparent_50%)]" />
        <div className="relative z-10 flex flex-col justify-between p-12 text-white w-full">
          <div className="flex items-center gap-3">
            <img src={MBE_LOGO} alt="MBE International" className="w-14 h-14 rounded-full object-cover ring-2 ring-orange-500/50" />
            <div className="font-display font-bold text-lg tracking-tight">
              <p>MBE International</p>
              <p className="text-orange-400 -mt-1 text-sm">Road To Bali Challenge</p>
            </div>
          </div>
          <div className="flex flex-col items-center text-center">
            <img
              src={MBE_LOGO}
              alt="MBE International"
              className="w-72 h-72 rounded-full object-cover mb-6 shadow-[0_0_60px_rgba(249,115,22,0.25)]"
            />
            <p className="text-xs uppercase tracking-[0.3em] text-blue-400 font-mono mb-2">Campaign Control Center</p>
            <h2 className="font-display text-3xl lg:text-4xl font-bold leading-tight max-w-md">
              200 traders. <span className="text-gradient">30 lots.</span> One trip to Bali.
            </h2>
            <p className="text-sm text-gray-400 mt-3 max-w-md">
              Track participants, validate broker reports, audit milestones, and orchestrate payouts — all from one operational dashboard.
            </p>
          </div>
          <div className="flex gap-8 text-xs font-mono text-gray-500">
            <div>
              <p className="text-2xl font-bold text-white">{campaign?.target_participants ?? "—"}</p>
              <p>Target Participants</p>
            </div>
            <div>
              <p className="text-2xl font-bold text-white">
                {campaign
                  ? `${new Date(campaign.campaign_start).toLocaleDateString("en-US", { month: "short" })}–${new Date(campaign.campaign_end).toLocaleDateString("en-US", { month: "short" })}`
                  : "—"}
              </p>
              <p>Campaign Window</p>
            </div>
          </div>
        </div>
      </div>

      {/* Right form */}
      <div className="flex-1 flex items-center justify-center p-6">
        <div className="w-full max-w-md">
          <div className="lg:hidden mb-8 flex items-center gap-3">
            <img src={MBE_LOGO} alt="MBE International" className="w-12 h-12 rounded-full object-cover ring-2 ring-orange-500/40" />
            <div className="font-display font-bold text-base text-white">MBE Road To Bali</div>
          </div>

          <p className="text-xs uppercase tracking-[0.3em] text-blue-400 font-mono mb-2">Operations Login</p>
          <h1 className="font-display text-3xl font-bold text-white mb-2">Sign in to your console</h1>
          <p className="text-sm text-gray-400 mb-8">Internal access. Enter your operations credentials.</p>

          <form onSubmit={submit} className="space-y-4">
            <div>
              <label className="block text-xs font-medium text-gray-400 mb-1.5 uppercase tracking-wider">Email</label>
              <input
                type="email"
                data-testid="login-email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                autoComplete="off"
                placeholder="you@company.com"
                className="w-full bg-[#0B0F19] border border-white/10 rounded-md px-4 py-2.5 text-white focus:border-blue-500 focus:ring-1 focus:ring-blue-500 outline-none transition-colors"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-400 mb-1.5 uppercase tracking-wider">Password</label>
              <div className="relative">
                <input
                  type={show ? "text" : "password"}
                  data-testid="login-password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  className="w-full bg-[#0B0F19] border border-white/10 rounded-md px-4 py-2.5 pr-10 text-white focus:border-blue-500 focus:ring-1 focus:ring-blue-500 outline-none transition-colors"
                />
                <button
                  type="button"
                  onClick={() => setShow(!show)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-white"
                >
                  {show ? <EyeSlash size={18} /> : <Eye size={18} />}
                </button>
              </div>
            </div>
            <button
              type="submit"
              disabled={submitting}
              data-testid="login-submit"
              className="w-full bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white font-medium py-2.5 rounded-md transition-colors shadow-[0_4px_20px_rgba(37,99,235,0.3)]"
            >
              {submitting ? "Signing in..." : "Sign in to Console"}
            </button>
          </form>

          <div className="mt-6 pt-6 border-t border-white/10">
            <p className="text-xs text-gray-500 mb-3">Public access</p>
            <div className="flex gap-3">
              <Link to="/public-leaderboard" data-testid="public-leaderboard-link" className="flex-1 text-center text-xs px-3 py-2 rounded-md border border-blue-500/30 bg-blue-500/5 text-blue-300 hover:bg-blue-500/10 transition-colors">
                Public Leaderboard →
              </Link>
              <Link to="/terms" data-testid="terms-link" className="flex-1 text-center text-xs px-3 py-2 rounded-md border border-orange-500/30 bg-orange-500/5 text-orange-300 hover:bg-orange-500/10 transition-colors">
                Terms &amp; Conditions →
              </Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
